import * as React from "react";
import { ArrowUpRightCircle, CloudDownload } from "react-bootstrap-icons";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import { Helmet } from "react-helmet-async";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import mapValues from "lodash/mapValues";
import sortBy from "lodash/sortBy";
import { useNavigate } from "react-router-dom";

import "../styles/responses.css";
import { Api, PageSpinner, useApi } from "../util";
import { AuthContext } from "../context";

const Responses = () => {
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState<string | null>(null);
  const [apiResponses, setApiResponses] = React.useState<Awaited<
    ReturnType<Api["getResponses"]>
  > | null>(null);

  const { fetch, setAuth } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const api = useApi(fetch!);

  React.useEffect(() => {
    void (async () => {
      setApiResponses(await api.getResponses());
      setLoading(false);
    })();
  }, [api]);

  const loginAs = React.useCallback(
    (email: string, accessToken: string) => {
      setAuth({ email, accessToken });
      navigate("/rsvp");
    },
    [navigate, setAuth]
  );

  if (loading || !apiResponses) {
    return <PageSpinner />;
  }

  const { responses, guests: unsortedGuests, updates } = apiResponses;
  const allGuests = sortBy(unsortedGuests, ({ email }) => email);

  const eventSlugs = Object.keys(responses);
  eventSlugs.sort(
    (a, b) => responses[a].event.start_date - responses[b].event.start_date
  );

  const eventCounts = Object.fromEntries(
    Object.values(responses).map(
      ({ event: { slug }, responses: eventResponses }) => [
        slug,
        `(${eventResponses.reduce(
          (a, b) => a + (b.attending ?? 0),
          0
        )}-${eventResponses.reduce(
          (a, b) => a + (b.attending === 0 ? 0 : b.attending ?? 1),
          0
        )}/${eventResponses.reduce(
          (a, b) =>
            a + (b.attending == null || b.attending === 0 ? 1 : b.attending),
          0
        )})`,
      ]
    )
  );

  const responsesByGuest = mapValues(responses, ({ responses }) =>
    Object.fromEntries(
      responses.map((response) => [response.guest, response.attending])
    )
  );

  const guestsById = Object.fromEntries(
    allGuests.map((guest) => [guest.id, guest])
  );

  const displayedSlugs = eventSlugs.filter(
    (slug) => filter == null || filter === slug
  );

  const guests = allGuests.filter(({ id }) =>
    displayedSlugs.some((slug) => id in responsesByGuest[slug])
  );

  const csv = btoa(
    [
      [
        "name",
        "email",
        ...displayedSlugs.map((slug) => responses[slug].event.name),
      ],
      ...guests.map((guest) => [
        guest.name,
        guest.email,
        ...displayedSlugs.map((slug) =>
          responsesByGuest[slug][guest.id] === null
            ? "!"
            : responsesByGuest[slug][guest.id]
        ),
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n")
  );

  return (
    <Container className="text-light responses" fluid>
      <Helmet>Responses</Helmet>
      <Row>
        <Col sm={2}>
          <div className="d-flex flex-column gap-3">
            {eventSlugs.map((slug) => (
              <div key={slug}>
                <div className="fw-bolder fs-5">
                  {responses[slug].event.name}
                </div>
                <div className="fw-light">{eventCounts[slug]}</div>
              </div>
            ))}
          </div>
          <hr />
          <div className="d-flex flex-column gap-2 mb-3">
            {updates.map(({ guest, timestamp, event, changes }) => (
              <div key={timestamp} style={{ fontSize: ".875rem" }}>
                <div className="fw-bolder">
                  {new Date(timestamp * 1e3).toLocaleString()}
                </div>
                <div className="fw-light">
                  <span className="fw-bolder">
                    {guestsById[guest].name} · {responses[event].event.name}
                  </span>
                  <ul className="mb-1 ps-3">
                    {changes.map((change, i) => (
                      <li key={i}>{change}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </Col>
        <Col sm={10}>
          <p className="d-flex gap-2 justify-content-between">
            <FloatingLabel className="text-dark flex-grow-1" label="Event">
              <Form.Select
                onChange={({ currentTarget: { value } }) =>
                  setFilter(value === "all" ? null : value)
                }
              >
                <option value="all">All</option>
                {eventSlugs.map((slug) => (
                  <option key={slug} value={slug}>
                    {responses[slug].event.name}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
            <Button
              as="a"
              className="d-flex align-items-center"
              download="responses.csv"
              href={`data:text/csv;base64,${csv}`}
            >
              <CloudDownload />
            </Button>
          </p>
          <Table bordered hover responsive size="sm" striped>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                {displayedSlugs.map((slug) => (
                  <th key={slug}>{responses[slug].event.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {guests.map(
                (
                  { id, name, email, access_token: accessToken } // eslint-disable-line camelcase
                ) => (
                  <tr key={email}>
                    <td className="guest-name">
                      {name}{" "}
                      {accessToken !== undefined ? (
                        <a
                          className="link-secondary"
                          href={`/rsvp?email=${email}&access-token=${accessToken}`}
                          onClick={() => loginAs(email, accessToken)}
                          title="Login as"
                        >
                          <ArrowUpRightCircle />
                        </a>
                      ) : null}
                    </td>
                    <td>{email}</td>
                    {displayedSlugs.map((slug) => {
                      let content = null;

                      const response = responsesByGuest[slug][id];
                      if (response === null) {
                        content = <Badge bg="warning">!</Badge>;
                      } else if (response >= 1) {
                        content = <Badge bg="success">{response}</Badge>;
                      } else if (response === 0) {
                        content = <Badge bg="danger">{response}</Badge>;
                      }

                      return <td key={slug}>{content}</td>;
                    })}
                  </tr>
                )
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
};

export default Responses;
